import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, Users, Handshake, BookOpen, Newspaper,
  MessageSquare, Mail, TrendingUp, ArrowRight, Loader2
} from 'lucide-react';
import api from '../../utils/api';

const StatCard = ({ label, value, icon: Icon, sub, color, to }) => (
  <Link to={to} className="bg-white rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-all duration-200 hover:-translate-y-0.5 border border-gray-100 block">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={18} />
      </div>
      <ArrowRight size={14} className="text-gray-300 mt-1" />
    </div>
    <div className="font-display text-2xl font-bold text-gray-900 mb-0.5">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    <div className="text-sm font-medium text-gray-600">{label}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </Link>
);

const StatusBadge = ({ status }) => {
  const map = {
    successful:'bg-emerald-100 text-emerald-700', pending:'bg-amber-100 text-amber-700',
    failed:'bg-red-100 text-red-700', unread:'bg-blue-100 text-blue-700',
    read:'bg-gray-100 text-gray-600', replied:'bg-emerald-100 text-emerald-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status]||'bg-gray-100 text-gray-600'}`}>{status}</span>;
};

export default function AdminDashboard() {
  const [stats,    setStats]    = useState({});
  const [donations,setDonations]= useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/donations/admin/all?limit=5'),
      api.get('/volunteers/admin/all?limit=1'),
      api.get('/partners/admin/all?limit=1'),
      api.get('/programs/admin/all?limit=1'),
      api.get('/blog/admin/all?limit=1'),
      api.get('/contact/admin/all?limit=5'),
      api.get('/newsletter/admin/all?limit=1'),
    ]).then(([don, vol, par, prog, blog, con, news]) => {
      setStats({
        donations:   don.data.total,
        raised:      don.data.items.filter(d => d.status==='successful').reduce((s,d) => s+d.amount, 0),
        volunteers:  vol.data.total,
        partners:    par.data.total,
        programs:    prog.data.total,
        blogs:       blog.data.total,
        contacts:    con.data.total,
        subscribers: news.data.total,
      });
      setDonations(don.data.items.slice(0,5));
      setContacts(con.data.items.slice(0,5));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const CARDS = [
    { label:'Donations / Support', value: stats.donations??'—', icon:DollarSign, color:'bg-gold-100 text-gold-700',   sub:`₦${(stats.raised||0).toLocaleString()} raised`, to:'/admin/donations' },
    { label:'Volunteers',          value: stats.volunteers??'—', icon:Users,      color:'bg-blue-100 text-blue-700',    sub:'Registered applicants', to:'/admin/volunteers' },
    { label:'Partners',            value: stats.partners??'—',   icon:Handshake,  color:'bg-emerald-100 text-emerald-700', sub:'Institutions & NGOs', to:'/admin/partners' },
    { label:'Programs',            value: stats.programs??'—',   icon:BookOpen,   color:'bg-violet-100 text-violet-700', sub:'Active & upcoming', to:'/admin/programs' },
    { label:'Publications',        value: stats.blogs??'—',      icon:Newspaper,  color:'bg-orange-100 text-orange-700', sub:'Blog posts & briefs', to:'/admin/blog' },
    { label:'Messages',            value: stats.contacts??'—',   icon:MessageSquare, color:'bg-red-100 text-red-700',  sub:'Contact enquiries', to:'/admin/contacts' },
    { label:'Subscribers',         value: stats.subscribers??'—',icon:Mail,       color:'bg-teal-100 text-teal-700',   sub:'Research updates list', to:'/admin/newsletter' },
    { label:'Impact Score',        value: '94%',                 icon:TrendingUp, color:'bg-royal-100 text-royal-700', sub:'Funds reach programs', to:'/admin/settings' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={28} className="animate-spin text-royal-800" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">SAAI Institute — admin overview.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Donations */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">Recent Donations</h3>
            <Link to="/admin/donations" className="text-xs text-royal-800 font-semibold hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-gray-50">
            {donations.length === 0
              ? <p className="text-center text-gray-400 text-sm py-8">No donations yet</p>
              : donations.map(d => (
                <div key={d._id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center text-gold-700 font-bold text-xs flex-shrink-0">
                    {(d.isAnonymous ? 'A' : d.donorName?.[0]) || 'D'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{d.isAnonymous ? 'Anonymous' : d.donorName}</div>
                    <div className="text-xs text-gray-400">{d.program || 'General Fund'}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-gray-900">₦{d.amount?.toLocaleString()}</div>
                    <StatusBadge status={d.status} />
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">Recent Messages</h3>
            <Link to="/admin/contacts" className="text-xs text-royal-800 font-semibold hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-gray-50">
            {contacts.length === 0
              ? <p className="text-center text-gray-400 text-sm py-8">No messages yet</p>
              : contacts.map(c => (
                <div key={c._id} className="flex items-center gap-3 px-5 py-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.status==='unread'?'bg-blue-500':'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{c.name}</div>
                    <div className="text-xs text-gray-400 truncate">{c.subject}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { to:'/admin/blog',      label:'+ New Publication' },
            { to:'/admin/programs',  label:'+ Add Program' },
            { to:'/admin/gallery',   label:'+ Upload to Gallery' },
            { to:'/admin/grants',    label:'+ Add Grant' },
            { to:'/admin/settings',  label:'⚙ Edit Site Content' },
          ].map(a => (
            <Link key={a.to} to={a.to}
              className="px-4 py-2 bg-gray-100 hover:bg-royal-800 hover:text-white text-gray-700 rounded-lg text-xs font-semibold transition-all duration-200">
              {a.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}